import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { FileText, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";
const empty = { name: "", subject: "", body: "" };

export function EmailTemplatesManager() {
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/email-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, subject: t.subject, body: t.body }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Template name is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/email-templates/${editing.id}`, form);
      else await api.post("/email-templates", form);
      toast.success(editing ? "Template updated" : "Template created");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save template");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/email-templates/${deleteTarget.id}`);
      toast.success("Template deleted");
      load();
    } catch { toast.error("Could not delete template"); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="email-templates-card">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" /> Email templates
        </h3>
        <button onClick={openNew} data-testid="add-template-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-3 py-1.5 rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> New
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">Shared one-click templates your team can use when composing client emails.</p>

      {templates.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">No templates yet. Create one to speed up common emails.</p>
      ) : (
        <div className="space-y-2" data-testid="templates-list">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 border border-gray-100 rounded-sm p-3 hover:bg-gray-50 group" data-testid={`template-${t.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                <p className="text-xs text-gray-500 truncate">{t.subject || "No subject"}</p>
              </div>
              <button onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => setDeleteTarget(t)} data-testid={`delete-template-${t.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-sm" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit template" : "New template"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <label className={labelCls}>Template name</label>
              <input data-testid="template-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="e.g. Payment reminder" />
            </div>
            <div>
              <label className={labelCls}>Subject</label>
              <input data-testid="template-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Body</label>
              <textarea data-testid="template-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} className={`${inputCls} resize-none`} />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={saving} data-testid="save-template-button" className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save" : "Create"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-template-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete template?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteTarget?.name}" will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-template-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
